import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jobApi, JobPosting } from '../services/recruitingApi';

export interface ListJobsParams {
  status?: string;
  department_id?: string;
  hiring_manager_id?: string;
  recruiter_id?: string;
  /** Only postings based at this office — the reverse of the location block's
   * office list (junction `recruiting_job_posting_offices`). */
  office_id?: string;
  search?: string;
  /** Comma-separated tag UUIDs (cross-cutting tags framework). */
  tags?: string;
  /** Tag matching mode: `any` (OR, default) or `all` (AND). */
  tags_mode?: 'any' | 'all';
  /** A key of `JOB_SORT_FIELDS` in modules/recruiting/repositories/job_repository.py. */
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  /** Admin list-scoping opt-out (§4.1): `'own'` narrows a Full-Access admin to their
   * own owner/shared jobs instead of the audited override view. Set from
   * `useAdminListScope`; undefined for everyone else. */
  admin_scope?: string;
}

export interface JobCreate {
  title: string;
  description?: string;
  /** Omit to use the tenant's default hiring pipeline (provisioned when absent). */
  pipeline_id?: string;
  company_id?: string;
  department_id?: string;
  employment_type?: string;
  experience_level?: string;
  location_type?: string;
  // New location scope fields
  location_scope?: 'offices' | 'countries' | 'regions' | 'global';
  location_office_ids?: string[];
  location_country_codes?: string[];
  location_regions?: string[];
  // Legacy fields (deprecated)
  location_city?: string;
  location_state?: string;
  location_country?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  salary_period?: string;
  show_salary?: boolean;
  requirements?: string;
  benefits?: string;
  hiring_manager_id?: string;
  recruiter_id?: string;
  application_notification_user_ids?: string[];
  closes_at?: string;
  target_hire_count?: number;
}

export type JobUpdate = Partial<JobCreate>;

export const jobKeys = {
  all: ['jobs'] as const,
  list: (params?: ListJobsParams) => [...jobKeys.all, 'list', params] as const,
  detail: (id: string) => [...jobKeys.all, 'detail', id] as const,
};

export function useJobs(params?: ListJobsParams) {
  return useQuery({
    queryKey: jobKeys.list(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => jobApi.list(params),
  });
}

export function useJob(id: string, options?: { include_pipeline?: boolean; include_questions?: boolean }) {
  return useQuery({
    queryKey: jobKeys.detail(id),
    queryFn: () => jobApi.get(id, options),
    enabled: !!id,
  });
}

export function useCreateJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: JobCreate) => jobApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useUpdateJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JobUpdate }) => jobApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useDuplicateJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title?: string }) => jobApi.duplicate(id, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function usePublishJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => jobApi.publish(id),
    onSuccess: (_: JobPosting, id: string) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useCloseJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, filled }: { id: string; filled?: boolean }) => jobApi.close(id, filled),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useReopenJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => jobApi.reopen(id),
    onSuccess: (_: JobPosting, id: string) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useDeleteJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => jobApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}
